import React, { useState, useEffect } from "react";
import { Check, ChevronRight, Pencil } from "lucide-react";
import { Input } from "@/components/ui/input";

/**
 * Question Engine — single-focus progression.
 *
 *  Answered rail (top)   compact one-line summaries, click to amend
 *  Active card (center)  one prominent question with ask_prompt
 *  Up next (bottom)      muted hint of the next 1-2 questions
 *
 * Pacing-first design: never more than one "loud" element on screen.
 */
export default function QuestionEngine({
  visibleQuestions,
  answers,
  disabled,
  onAnswerChange,
}) {
  const total = visibleQuestions.length;
  // First unanswered is the active one. If all answered, last question stays active.
  const firstUnansweredIdx = visibleQuestions.findIndex((q) => isEmpty(answers[q.id]));
  const defaultIdx = firstUnansweredIdx === -1 ? Math.max(0, total - 1) : firstUnansweredIdx;
  const [activeIdx, setActiveIdx] = useState(defaultIdx);

  // Re-sync when the visible question set changes (follow-ups unlocking, etc.)
  useEffect(() => {
    if (firstUnansweredIdx !== -1) setActiveIdx(firstUnansweredIdx);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleQuestions.length]);

  if (total === 0) {
    return (
      <div className="text-sm text-slate-500 italic">Loading questions…</div>
    );
  }
  const activeQ = visibleQuestions[activeIdx];

  return (
    <div className="space-y-4" data-testid="question-engine">
      {/* Answered rail */}
      {activeIdx > 0 && (
        <ol className="space-y-1" data-testid="answered-rail">
          {visibleQuestions.slice(0, activeIdx).map((q, i) => (
            <li key={q.id}>
              <button
                type="button"
                onClick={() => setActiveIdx(i)}
                className="w-full flex items-start gap-2.5 text-left text-[16px] text-slate-700 hover:text-slate-900 hover:bg-slate-50 rounded px-3 py-2.5 transition-colors group"
                data-testid={`answered-row-${q.id}`}
              >
                <Check className="w-4 h-4 mt-1 text-emerald-600 flex-shrink-0" />
                <span className="flex-1 truncate">
                  <span className="text-slate-500">{q.label}</span>
                  <span className="text-slate-900 font-semibold"> · {renderShortAnswer(q, answers[q.id])}</span>
                </span>
                <Pencil className="w-4 h-4 text-slate-300 group-hover:text-slate-600 mt-1 flex-shrink-0" />
              </button>
            </li>
          ))}
        </ol>
      )}

      {/* Active question card */}
      <ActiveQuestionCard
        q={activeQ}
        index={activeIdx + 1}
        total={total}
        value={answers[activeQ.id]}
        disabled={disabled}
        onChange={(v) => onAnswerChange(activeQ, v)}
        onAdvance={() => {
          // Advance to next question if not at the end
          if (activeIdx < total - 1) setActiveIdx(activeIdx + 1);
        }}
      />

      {/* Up next preview — muted */}
      {activeIdx < total - 1 && (
        <div className="pt-2 space-y-1" data-testid="up-next">
          <p className="text-[12.5px] uppercase tracking-[0.18em] text-slate-500 font-semibold pl-2">
            Up next
          </p>
          {visibleQuestions.slice(activeIdx + 1, activeIdx + 3).map((q) => (
            <p
              key={q.id}
              className="text-[16px] text-slate-500 truncate pl-2"
              data-testid={`upnext-${q.id}`}
            >
              {q.label}
            </p>
          ))}
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Active question card — large, single focus
// ---------------------------------------------------------------------------

function ActiveQuestionCard({ q, index, total, value, disabled, onChange, onAdvance }) {
  const ask = q.ask_prompt || q.label;
  const showLabel = q.ask_prompt && q.ask_prompt !== q.label;
  const progressPct = Math.min(100, Math.round((index / Math.max(1, total)) * 100));
  const handleChange = (v) => {
    onChange(v);
    // Decisive answer types auto-advance briefly after selection.
    // Text answers never auto-advance — the FOH may still be typing.
    if (!disabled && q.type !== "text") {
      // Allow react state to flush first; tightened to 180ms for snappier chip pacing.
      setTimeout(onAdvance, 180);
    }
  };

  return (
    <article
      className="rounded-xl border border-slate-200 bg-white shadow-sm"
      data-testid={`active-question-${q.id}`}
    >
      {/* Thin slate progress bar — subtle pacing cue */}
      <div
        className="h-0.5 w-full bg-slate-100 rounded-t-xl overflow-hidden"
        data-testid="active-question-progress"
        aria-label={`Question ${index} of ${total}`}
      >
        <div
          className="h-full bg-slate-400 transition-all duration-300 ease-out"
          style={{ width: `${progressPct}%` }}
        />
      </div>
      <header className="px-7 pt-5 pb-2.5 flex items-center gap-2.5">
        <span className="text-[13px] font-mono text-slate-500 tracking-wider font-semibold">
          {String(index).padStart(2, "0")} / {String(total).padStart(2, "0")}
        </span>
        {q.required && (
          <span
            className="inline-flex items-center gap-1.5 text-[13px] text-amber-700 ml-1 font-semibold"
            data-testid="required-marker"
            title="Required to complete intake"
          >
            <span className="w-2 h-2 rounded-full bg-amber-500" />
            <span className="tracking-wide">Needed</span>
          </span>
        )}
      </header>
      <div className="px-7 pb-7">
        <h2 className="text-[26px] sm:text-[28px] font-bold text-slate-900 leading-snug tracking-tight">
          {ask}
        </h2>
        {showLabel && (
          <p className="text-[13.5px] text-slate-500 mt-2.5 leading-snug">
            <span className="uppercase tracking-wider font-semibold">Captured as: </span>
            <span className="font-medium">{q.label}</span>
          </p>
        )}
        {q.foh_hint && (
          <p className="text-[16px] text-slate-600 italic mt-3.5 leading-relaxed">
            {q.foh_hint}
          </p>
        )}
        {q.trust_prompt && (
          <p className="text-[16.5px] text-teal-800 italic mt-3.5 leading-relaxed pl-4 border-l-2 border-teal-300">
            {q.trust_prompt}
          </p>
        )}

        <div className="mt-6">
          <AnswerInput q={q} value={value} disabled={disabled} onChange={handleChange} />
        </div>

        {!disabled && !q.required && (
          <button
            type="button"
            onClick={onAdvance}
            className="mt-5 text-[13.5px] text-slate-400 hover:text-slate-700 inline-flex items-center"
            data-testid="skip-question-btn"
          >
            Skip <ChevronRight className="w-4 h-4 ml-0.5" />
          </button>
        )}
      </div>
    </article>
  );
}

// ---------------------------------------------------------------------------
// Answer inputs — chip-first, low-typing
// ---------------------------------------------------------------------------

function AnswerInput({ q, value, disabled, onChange }) {
  if (q.type === "single") {
    return (
      <div className="flex flex-wrap gap-2">
        {(q.options || []).map((opt) => {
          const selected = value === opt;
          return (
            <ChipButton
              key={opt}
              selected={selected}
              disabled={disabled}
              onClick={() => onChange(selected ? null : opt)}
              testId={`intake-opt-${q.id}-${opt}`}
            >
              {opt}
            </ChipButton>
          );
        })}
      </div>
    );
  }

  if (q.type === "multi") {
    const arr = Array.isArray(value) ? value : [];
    return (
      <div className="flex flex-wrap gap-2">
        {(q.options || []).map((opt) => {
          const selected = arr.includes(opt);
          return (
            <ChipButton
              key={opt}
              selected={selected}
              disabled={disabled}
              onClick={() => {
                const next = selected ? arr.filter((a) => a !== opt) : [...arr, opt];
                onChange(next);
              }}
              testId={`intake-opt-${q.id}-${opt}`}
            >
              {opt}
            </ChipButton>
          );
        })}
      </div>
    );
  }

  if (q.type === "boolean") {
    return (
      <div className="flex gap-2.5">
        {[
          { label: "Yes", val: true },
          { label: "No", val: false },
        ].map((b) => {
          const selected = value === b.val;
          return (
            <button
              key={b.label}
              type="button"
              disabled={disabled}
              onClick={() => onChange(selected ? null : b.val)}
              className={`px-8 py-3 rounded-md text-[17px] font-semibold border-2 transition-all ${
                selected
                  ? "bg-slate-900 border-slate-900 text-white"
                  : "bg-white border-slate-300 text-slate-700 hover:border-slate-500"
              } disabled:opacity-50 disabled:cursor-not-allowed`}
              data-testid={`intake-bool-${q.id}-${b.label}`}
            >
              {b.label}
            </button>
          );
        })}
      </div>
    );
  }

  if (q.type === "text") {
    return (
      <Input
        value={value || ""}
        onChange={(e) => onChange(e.target.value)}
        disabled={disabled}
        placeholder="Type the answer…"
        className="text-[17px] py-3 px-4"
        data-testid={`intake-text-${q.id}`}
      />
    );
  }

  return null;
}

function ChipButton({ selected, disabled, onClick, children, testId }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={`text-[17px] font-medium px-5 py-2.5 rounded-full border-2 transition-all ${
        selected
          ? "bg-slate-900 border-slate-900 text-white shadow-sm"
          : "bg-white border-slate-300 text-slate-800 hover:border-slate-500 hover:bg-slate-50"
      } disabled:opacity-50 disabled:cursor-not-allowed`}
      data-testid={testId}
    >
      {children}
    </button>
  );
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function isEmpty(v) {
  return v === undefined || v === null || v === "" || (Array.isArray(v) && v.length === 0);
}

function renderShortAnswer(q, v) {
  if (isEmpty(v)) return <span className="text-slate-300">—</span>;
  if (q.type === "boolean") return v ? "Yes" : "No";
  if (Array.isArray(v)) return v.join(", ");
  return String(v);
}
